import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "../../lib/api";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import { ThemedShell, ThemedCard } from "../../components/child/ThemedShell";
import { Button } from "../../components/ui/button";
import { sfx } from "../../lib/sound";

export default function Shop() {
  const navigate = useNavigate();
  const { theme, terms } = useTheme();
  const { refresh } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/child/shop").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  const buy = async (item) => {
    setBusy(item.id);
    try {
      await api.post("/child/shop/buy", { item_id: item.id });
      sfx.coin();
      toast.success(`${item.name} gekocht! 🎉`);
      await load();
    } catch (err) { sfx.wrong(); toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const equip = async (item) => {
    setBusy(item.id);
    try {
      await api.post("/child/shop/equip", { item_id: item.id });
      sfx.select();
      toast.success(`${item.name} uitgerust!`);
      await load();
      refresh();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const muted = { color: "var(--t-muted)" };
  const equipped = data?.items?.find((i) => i.equipped);

  return (
    <ThemedShell>
      <header className="sticky top-0 z-30 border-b backdrop-blur-md" style={{ background: `color-mix(in srgb, var(--t-surface) 75%, transparent)`, borderColor: "var(--t-border)" }}>
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/leerling")} className="rounded-full font-bold" style={{ color: "var(--t-text)" }} data-testid="shop-back-btn"><ArrowLeft className="w-5 h-5 mr-1" /> {terms.dashboard}</Button>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{ background: "var(--t-surface2)" }} data-testid="shop-coins">
            <span className="text-lg">{terms.coinIcon}</span><span className="font-heading font-extrabold">{data?.coins ?? "…"}</span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 pt-8 pb-16">
        <div className="flex items-center gap-4 mb-8">
          <motion.div animate={{ y: [0, -8, 0] }} transition={{ repeat: Infinity, duration: 3 }} className="w-20 h-20 rounded-3xl flex items-center justify-center text-5xl" style={{ background: "var(--t-surface2)" }} data-testid="shop-equipped-avatar">{equipped?.emoji || theme.mascot}</motion.div>
          <div>
            <h1 className="font-heading text-3xl font-black themed-heading">Beloningswinkel</h1>
            <p className="font-semibold" style={muted}>Verdien munten met oefenen en kies je eigen avatar! 🛍️</p>
          </div>
        </div>

        {!data ? (
          <div className="flex justify-center py-16"><div className="w-12 h-12 rounded-full border-4 animate-spin" style={{ borderColor: "var(--t-surface2)", borderTopColor: "var(--t-accent)" }} /></div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {data.items.map((item) => {
              const canBuy = !item.owned && data.coins >= item.price;
              return (
                <ThemedCard key={item.id} className="p-4 flex flex-col items-center text-center" data-testid={`shop-item-${item.id}`}>
                  <div className="relative w-20 h-20 rounded-3xl flex items-center justify-center text-5xl mb-2" style={{ background: "var(--t-surface2)", opacity: item.owned ? 1 : 0.92 }}>
                    {item.emoji}
                    {item.equipped && <span className="absolute -top-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "var(--t-success)", color: "#fff" }}><Check className="w-4 h-4" strokeWidth={3} /></span>}
                  </div>
                  <p className="font-heading font-extrabold">{item.name}</p>
                  {item.equipped ? (
                    <span className="mt-2 text-sm font-bold" style={{ color: "var(--t-success)" }} data-testid={`shop-equipped-${item.id}`}>In gebruik</span>
                  ) : item.owned ? (
                    <Button onClick={() => equip(item)} disabled={busy === item.id} className="mt-2 w-full rounded-full font-bold h-9 border-0" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }} data-testid={`equip-${item.id}`}>Uitrusten</Button>
                  ) : (
                    <Button onClick={() => buy(item)} disabled={!canBuy || busy === item.id} className="mt-2 w-full rounded-full font-bold h-9 border-0 disabled:opacity-50" style={{ background: canBuy ? "var(--t-accent)" : "var(--t-surface2)", color: canBuy ? "var(--t-on-accent)" : "var(--t-muted)" }} data-testid={`buy-${item.id}`}>
                      {!canBuy && <Lock className="w-3.5 h-3.5 mr-1" />} {terms.coinIcon} {item.price}
                    </Button>
                  )}
                </ThemedCard>
              );
            })}
          </div>
        )}
      </main>
    </ThemedShell>
  );
}
